/**
 * Which API this viewer talks to.
 *
 * There is one deployment of this app and, by choice, no staging copy. Staging
 * also runs on a copy of the live databases, so a proposal emailed from staging
 * used to open here against the live API: the customer saw their real proposal
 * and could sign it. A test run could accept a real contract.
 *
 * The backend now marks a link it sent from anywhere other than the live API
 * (see App\Support\NotificationLink), and that marker chooses the API here.
 *
 * The marker is a flag, never an address. It selects from the two hosts below
 * and nothing else: a link that could name its own API host would let anyone
 * who can send a customer a URL choose where that customer's signature goes.
 * Adding an environment means adding it here, in a deployed build.
 */

const LIVE_API = "https://api.cleancinch.com";
const STAGING_API = "https://api-staging.cleancinch.com";

/** Whether the link the customer followed was sent from staging. */
export const isStagingLink = (search?: string): boolean => {
  const query =
    search ?? (typeof window === "undefined" ? "" : window.location.search);

  return new URLSearchParams(query).get("staging") === "true";
};

/**
 * The API host, with no /api suffix.
 *
 * A marked link pins staging. Otherwise the build's own REACT_APP_API_URL
 * still wins, which is what keeps a local build pointed at a local API.
 */
export const apiRoot = (): string => {
  if (isStagingLink()) return STAGING_API;

  const configured = process.env.REACT_APP_API_URL;

  return configured
    ? configured.replace(/\/api\/?$/, "")
    : LIVE_API;
};

/** The API base every request is built on, including the /api suffix. */
export const apiUrl = (): string => `${apiRoot()}/api`;
